import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input } from '@angular/core';
import { formatSeconds } from '@common/helpers';
import { MListData } from '@common/interfaces';
import { ServicingCommonStoreService } from '@common/services';
import { MyBookingItineraryService } from '@common/services/my-booking/my-booking-itinerary.service';
import { TripSummaryService } from '@common/services/my-booking/trip-summary.service';
import { SupportComponent } from '@lib/components/support-class';
import { DeviceType } from '@lib/interfaces';
import { DateFormatPipe, StaticMsgPipe } from '@lib/pipes';
import { CommonLibService } from '@lib/services';
import { Subscription } from 'rxjs';
import { Bound, GetOrderResponseData, GetOrderResponseDataOrderType } from 'src/sdk-servicing';

@Component({
  selector: 'asw-bound-title',
  templateUrl: './bound-title.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BoundTitleComponent extends SupportComponent {
  @Input() boundIdx!: number;

  bound: Bound = {};
  boundStatus: Array<{ label: string; reasons?: string }> = [];

  private _deviceType: string = '';
  private _subscriptions = new Subscription();
  private _reasonCodesMaster: Array<MListData> = [];
  // 提携：DynamicWaiver情報表示フラグ
  private _isShowDynamicWaiver: Boolean = true;

  get boundDuration(): string {
    return formatSeconds(this.bound.duration);
  }

  constructor(
    public tripSummarySvc: TripSummaryService,
    private _myBookingItineraryService: MyBookingItineraryService,
    private _servicingCommonStoreService: ServicingCommonStoreService,
    private _dateFormat: DateFormatPipe,
    private _staticMsg: StaticMsgPipe,
    private _change: ChangeDetectorRef,
    private _common: CommonLibService
  ) {
    super(_common);
    this._deviceType = _common.aswContextStoreService.aswContextData.deviceType ?? '';
  }

  override init(): void {
    this._subscriptions.add(
      this._servicingCommonStoreService.getServicingCommonModel$().subscribe((servicingCommon) => {
        const pnr = (servicingCommon.getOrder as GetOrderResponseData) ?? {};
        this.bound = pnr.air?.bounds?.at(this.boundIdx) ?? {};
        // 提携：DynamicWaiver情報表示フラグを設定する。
        this.setShowDynamicWaiver(pnr);
        this._change.markForCheck();
      })
    );
    this._subscriptions.add(
      this._myBookingItineraryService.masterData$.subscribe((result) => {
        this._reasonCodesMaster = result.flightInfoReasonCodes ?? [];
        this.getBoundStatus();
        this._change.markForCheck();
      })
    );
  }

  // 提携：国内代理店作成PNR、グループPNR、社員用PNRの場合、DynamicWaiver情報を表示しないようにする。(INVOL-MYBOOKING-003)
  setShowDynamicWaiver(pnr: GetOrderResponseData): void {
    this._isShowDynamicWaiver = true;
    if (
      pnr?.orderType?.channelType === GetOrderResponseDataOrderType.ChannelTypeEnum.Nh3 ||
      pnr?.orderType?.isGroupBooking ||
      pnr?.orderType?.isStaffBooking
    ) {
      this._isShowDynamicWaiver = false;
    }
  }

  // (1.1.2) 欠航・遅延・早発情報
  getBoundStatus(): void {
    this.boundStatus = [];
    // (1.1.3) 当該バウンド.flights[]の中にstatusCode="UN"が存在する、または、当該バウンド.isFlix=true(FLIX便が含まれる)場合、表示する。
    const isConditionTrue = this.bound?.flights?.some((flight) => flight.statusCode === 'UN') || this.bound?.isFlix;
    if (isConditionTrue)
      // (1.1.4) 欠航案内 & (1.1.5) 欠航理由
      this.updateStatusList('message.operationStatus.cancelled', this.bound.cancel?.reasonCodes);
    // (1.1.6) 遅延表示 - 当該バウンド.delay.isContainedSegment=trueの場合、表示する。
    if (this.bound.delay?.isContainedSegment)
      // (1.1.7) 遅延案内 & (1.1.8) 遅延理由
      this.updateStatusList('message.operationStatus.delayed', this.bound.delay?.reasonCodes);
    // (1.1.9) 早発表示 - 当該バウンド.earlyDeparture.isContainedSegment=trueの場合、表示する。
    if (this.bound.earlyDeparture?.isContainedSegment)
      // (1.1.10) 早発案内 & (1.1.11) 早発理由
      this.updateStatusList('message.operationStatus.earlyDeparture', this.bound.earlyDeparture?.reasonCodes);
  }

  // (1.1.5) 欠航理由 & (1.1.8) 遅延理由 & (1.1.11) 早発理由
  updateStatusList(status: string, reasonCodes: string[] = []): void {
    this.boundStatus.push({
      label: status,
      reasons: this._reasonCodesMaster
        // 1) 当該コードを基に「イベントID(1)」で取得した運航情報理由リストをフィルタして、当該運航情報理由を取得する。
        .filter((item) => reasonCodes.indexOf(item.value) !== -1)
        .map((item) => item.display_content)
        // 2) ","で区切る当該運航情報理由を連携することで早発理由を作成する。
        .filter(Boolean)
        .join(','),
    });
  }

  // (1.1.12) Dynamic Waiver対象
  getDynamicWaiver(): string {
    // 提携：国内代理店作成PNR、グループPNR、社員用PNRの場合、DynamicWaiver情報を表示しないようにする。(INVOL-MYBOOKING-003)
    if (!this._isShowDynamicWaiver) {
      return '';
    }

    const isRefund = this.bound.isContainedDynamicWaiverRefundFlight;
    const isChange = this.bound.isContainedDynamicWaiverChangeFlight;
    /** 1)
     * 当該バウンド.isContainedDynamicWaiverRefundFlight = false、かつ
     * 当該バウンド.isContainedDynamicWaiverChangeFlight = true の場合、
     * 変更手数料免除の旨を表示する。
     */
    if (!isRefund && isChange) return 'label.dynamicWaiverChange';
    /** 2)
     * 当該バウンド.isContainedDynamicWaiverChangeFlight = false、かつ
     * 当該バウンド.isContainedDynamicWaiverRefundFlight = true の場合、
     * 払戻手数料免除の旨を表示する。
     */
    if (!isChange && isRefund) return 'label.dynamicWaiverRefund';
    /** 3)
     * 当該バウンド.isContainedDynamicWaiverRefundFlight = true、かつ
     * 当該バウンド.isContainedDynamicWaiverChangeFlight = true の場合、
     * 変更・払戻手数料免除の旨を表示する。
     */
    if (isRefund && isChange) return 'label.dynamicWaiverChangeAndRefund';
    return '';
  }

  // (1.1.13) 出発日(バウンド)
  getOriginDepartureDateTime(): string {
    return this.bound.originDepartureDateTime
      ? // 1) 当該バウンド.originDepartureDateTimeが存在する場合、当該バウンド.originDepartureDateTimeを表示する。
        this._dateFormat.transform(this.bound.originDepartureDateTime, 'default_itineraryRsDate')
      : // 2) 当該バウンド.originDepartureDateTimeが存在しない場合、未定の旨を表示する。
        this._staticMsg.transform('label.hyphen');
  }

  // (1.1.65) ヘルプアイコン
  getFeeWaiverTooltip(): string {
    switch (this._deviceType) {
      // 1) ユーザ共通情報.data.aswContext.deviceType=“SP”版の場合、"tooltip.feeWaiver.sp"を表示する。
      case DeviceType.SMART_PHONE:
        return 'tooltip.feeWaiver.sp';
      // 2) ユーザ共通情報.data.aswContext.deviceType=“TAB”版の場合、"tooltip.feeWaiver.tab"を表示する。
      case DeviceType.TABLET:
        return 'tooltip.feeWaiver.tab';
      // 3) ユーザ共通情報.data.aswContext.deviceType=“PC”版の場合、"tooltip.feeWaiver.pc"を表示する。
      case DeviceType.PC:
        return 'tooltip.feeWaiver.pc';
    }
    return '';
  }

  override reload(): void {
    // Add comment to passed SonarLint
  }

  override destroy(): void {
    if (this._subscriptions) {
      this._subscriptions.unsubscribe();
    }
  }
}
